<template>
  <div class="earn">
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
      <h2 class="earn-title">{{ $t("finance_tab_earnings") }}</h2>
      <v-spacer />
      <finance-period-picker :value="period" @input="$emit('update:period', $event)" />
      <finance-export-menu
        :filename="exportFilename"
        :get-data="getExportData"
        :disabled="!overview"
      />
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <v-skeleton-loader v-if="loading && !overview" type="card, list-item@4" />

    <template v-else-if="overview">
      <v-alert v-if="overview.checkFailed" type="warning" text dense class="mb-4">
        {{ $t("finance_statement_check_warning") }}
      </v-alert>

      <!-- Jedan broj koji odgovara na pitanje "koliko ću dobiti i kad" + KPI kartice -->
      <v-card outlined class="earn-top pa-5 mb-4">
        <v-row align="center">
          <v-col cols="12" md="5">
            <template v-if="balance !== null">
              <div class="earn-label" :style="{ color: heroColor }">
                {{
                  balance >= 0
                    ? $t("finance_hero_you_receive")
                    : $t("finance_hero_you_owe")
                }}
              </div>
              <div class="earn-value" :style="{ color: heroColor }">
                {{ money(Math.abs(balance), overview.currency) }}
              </div>
              <div class="earn-hint">
                {{ settlementHint }}
                <a class="earn-link" @click="$emit('navigate', 'settlements')">
                  ·
                  {{
                    balance < 0 ? $t("finance_how_to_pay") : $t("finance_payout_history")
                  }}
                </a>
              </div>
            </template>
            <div v-else class="earn-hint">{{ $t("finance_balance_unavailable") }}</div>
          </v-col>

          <v-col cols="12" md="7">
            <div class="kpi-grid">
              <div v-for="kpi in kpiCards" :key="kpi.key" class="kpi-box">
                <div class="kpi-box-label">{{ translate(kpi.label) }}</div>
                <div class="kpi-box-value">{{ kpi.text }}</div>
                <div
                  v-if="kpi.trend !== null"
                  class="kpi-box-note"
                  :style="{ color: kpi.trend >= 0 ? positive : negative }"
                >
                  {{ kpi.trend >= 0 ? "▲ " : "▼ " }}{{ Math.abs(kpi.trend) }}%
                  {{ $t("finance_trend_vs_previous") }}
                </div>
                <div v-else-if="kpi.note" class="kpi-box-note">{{ kpi.note }}</div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-row>
        <v-col cols="12" md="7">
          <v-card outlined class="pa-5 fill-height">
            <div class="d-flex flex-wrap align-center mb-2" style="gap: 4px 12px">
              <div class="text-subtitle-1 font-weight-bold">
                {{ $t("finance_statement_for_period") }}
              </div>
              <span class="text-caption grey--text text--darken-1">
                {{ localeDate(period.from) }} - {{ localeDate(period.to) }} ·
                {{ $t("finance_click_row_hint") }}
              </span>
              <v-spacer />
              <span
                v-if="overview.checkOk"
                class="text-caption"
                :style="{ color: positive }"
              >
                ✓ {{ $t("finance_statement_matches") }}
              </span>
            </div>

            <statement-breakdown :overview="overview" @line-click="openLines" />
            <p
              v-if="!overview.rows.length"
              class="text-body-2 grey--text text--darken-1 mt-3 mb-0"
            >
              {{ $t("finance_no_activity_in_period") }}
            </p>

            <div class="earn-note mt-4">
              <v-icon x-small class="mr-2">mdi-information-outline</v-icon>
              {{ $t("finance_posting_note") }}
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card outlined class="pa-5 mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              {{ $t("finance_turnover_by_day_title") }}
            </div>
            <apexchart
              v-if="dailySeries.some((p) => p.value > 0)"
              type="bar"
              height="190"
              :options="chartOptions"
              :series="chartSeries"
            />
            <empty-state
              v-else
              icon="mdi-chart-bar"
              :title="$t('finance_no_activity_in_period')"
            />
          </v-card>

          <v-card outlined class="pa-5">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              {{ $t("finance_recent_activity_title") }}
            </div>
            <empty-state
              v-if="!overview.activity.length"
              icon="mdi-history"
              :title="$t('finance_no_recent_activity')"
            />
            <div v-else>
              <div
                v-for="activity in overview.activity"
                :key="activity.id"
                class="activity-row"
              >
                <span class="activity-time">{{ shortDate(activity.at) }}</span>
                <span class="activity-title">
                  <template v-for="(part, i) in activityParts(activity)">
                    <a
                      v-if="part.orderId"
                      :key="i"
                      class="earn-link"
                      @click="openOrder(part.orderId)"
                      >{{ part.text }}</a
                    >
                    <span v-else :key="i">{{ translate(part.text) }}</span>
                  </template>
                </span>
                <span
                  v-if="activity.amount != null"
                  class="activity-amount"
                  :style="{ color: activityAmount(activity) >= 0 ? positive : negative }"
                >
                  {{ money(activityAmount(activity), overview.currency, true) }}
                </span>
              </div>
              <a
                class="earn-link d-inline-block mt-3"
                @click="$emit('navigate', 'transactions')"
              >
                {{ $t("finance_all_orders") }} →
              </a>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <empty-state
      v-else-if="!restaurantId"
      icon="mdi-store-outline"
      :title="$t('finance_select_restaurant')"
    />

    <statement-lines-dialog
      v-model="linesOpen"
      :restaurant-id="restaurantId"
      :category="linesRow ? linesRow.key : ''"
      :label="linesRow ? linesRow.label : ''"
      :from="period.from"
      :to="period.to"
      :currency="overview ? overview.currency : ''"
      @open-order="openOrder"
    />
    <finance-order-detail-dialog v-model="orderOpen" :order-id="orderId" />
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import EmptyState from "@/components/global/EmptyState.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantFinanceService from "@/services/restaurantFinance";
import restaurantService from "@/services/restaurant";
import FinancePeriodPicker from "./FinancePeriodPicker.vue";
import FinanceExportMenu from "./FinanceExportMenu.vue";
import FinanceOrderDetailDialog from "./FinanceOrderDetailDialog.vue";
import StatementBreakdown from "./StatementBreakdown.vue";
import StatementLinesDialog from "./StatementLinesDialog.vue";
import hasFinanceContext from "./hasFinanceContext";
import { parseOverview } from "./overview";
import {
  FLOW_COLORS,
  PAYOUT_SCHEDULE_LABEL_KEYS,
  fillDailySeries,
  nextSettlementDate,
} from "./constants";

const KPI_ORDER = ["turnover", "order_count", "commission", "subscription"];
const MONEY_KPIS = ["turnover", "commission", "subscription"];

// "275.70 KM" / "2.380,00" / 50 -> broj (ili null ako nije čisto numerička vrijednost)
const toNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/[^\d.,-]/g, "");
  if (!cleaned) return null;
  const normalized =
    cleaned.includes(",") && cleaned.includes(".")
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

// Prefiks "earn-" jer globalni .hero (assets/styles/global.css) lomi raspored.
export default {
  name: "FinanceScreenEarnings",

  components: {
    apexchart: VueApexCharts,
    EmptyState,
    FinancePeriodPicker,
    FinanceExportMenu,
    FinanceOrderDetailDialog,
    StatementBreakdown,
    StatementLinesDialog,
  },

  mixins: [hasSnackbar, hasFinanceContext],

  props: {
    period: { type: Object, required: true },
  },

  data: () => ({
    loading: false,
    overview: null,
    contract: null,
    services: [],
    linesOpen: false,
    linesRow: null,
    orderOpen: false,
    orderId: null,
    positive: FLOW_COLORS.positive,
    negative: FLOW_COLORS.negative,
  }),

  computed: {
    balance() {
      return this.overview ? this.overview.currentBalance : null;
    },
    heroColor() {
      if (this.balance === null) return FLOW_COLORS.cash;
      return this.balance >= 0 ? FLOW_COLORS.positive : FLOW_COLORS.owing;
    },
    // Dinamika isplate je iz ugovora (services -> contract.payout_schedule); sljedeći datum
    // znamo samo za dnevnu i mjesečnu dinamiku.
    settlementHint() {
      const schedule = this.contract?.payout_schedule;
      if (!schedule) return this.$t("finance_schedule_not_agreed");
      let scheduleLabel = this.$t(
        PAYOUT_SCHEDULE_LABEL_KEYS[schedule] || "finance_schedule_not_agreed"
      );
      if (schedule === "THRESHOLD" && this.contract.payout_threshold_amount != null) {
        scheduleLabel = this.$t("finance_schedule_threshold_amount", {
          amount: this.money(
            this.contract.payout_threshold_amount,
            this.overview?.currency
          ),
        });
      }
      const next = nextSettlementDate(schedule);
      return next
        ? this.$t("finance_current_balance_next", {
            date: this.localeDate(next),
            schedule: scheduleLabel,
          })
        : this.$t("finance_current_balance_schedule", { schedule: scheduleLabel });
    },
    // Backend šalje vrijednost KPI-ja ponekad kao gotov tekst ("275.70 KM"), ponekad kao goli
    // broj - brojeve formatiramo sami, sa valutom iz odgovora.
    kpiCards() {
      const kpis = (this.overview?.kpis || []).filter((k) => k.key !== "balance");
      const ordered = KPI_ORDER.map((key) => kpis.find((k) => k.key === key)).filter(
        Boolean
      );
      const list = ordered.length ? ordered : kpis.slice(0, 4);
      const currency = this.overview?.currency;
      const orderCount = toNumber(kpis.find((k) => k.key === "order_count")?.value);
      return list.map((kpi) => {
        const num = toNumber(kpi.value);
        const isPlainNumber =
          typeof kpi.value === "number" ||
          (typeof kpi.value === "string" && /^[\d.,\s-]+$/.test(kpi.value));
        let text = String(kpi.value ?? "—");
        if (isPlainNumber && num !== null) {
          text =
            kpi.key === "order_count"
              ? String(Math.round(num))
              : this.money(num, currency);
        }
        let note = "";
        if (kpi.key === "order_count" && orderCount) {
          const turnover = toNumber(kpis.find((k) => k.key === "turnover")?.value);
          if (turnover !== null)
            note = this.$t("finance_average_amount", {
              amount: this.money(turnover / orderCount, currency),
            });
        } else if (kpi.key === "commission") {
          const service = this.services.find(
            (s) => s.is_active && s.pricing && Number(s.pricing.paying_type) === 2
          );
          if (service) note = `${this.translate(service.pricing.opis)} ${this.$t("finance_agreed")}`;
        } else if (kpi.key === "subscription") {
          const service = this.services.find(
            (s) => s.is_active && s.pricing && Number(s.pricing.paying_type) === 1
          );
          if (service) note = this.$t("finance_schedule_monthly");
        }
        return {
          key: kpi.key,
          label: kpi.label,
          text,
          trend:
            kpi.trend_percent !== null && kpi.trend_percent !== undefined
              ? Number(kpi.trend_percent)
              : null,
          note,
        };
      });
    },
    dailySeries() {
      return fillDailySeries(this.overview?.daily, this.period.from, this.period.to);
    },
    chartSeries() {
      return [
        {
          name: this.$t("finance_turnover_series_label"),
          data: this.dailySeries.map((p) => p.value),
        },
      ];
    },
    chartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: "inherit" },
        plotOptions: { bar: { borderRadius: 2, columnWidth: "70%" } },
        colors: ["#ff6e3a"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.dailySeries.map((p) =>
            p.label.slice(5).split("-").reverse().join(".")
          ),
          labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
          axisTicks: { show: false },
          tickAmount: 3,
        },
        yaxis: { show: false },
        tooltip: { y: { formatter: (v) => this.money(v, this.overview?.currency) } },
        grid: { show: false },
      };
    },
    exportFilename() {
      return `zarada_${this.period.from}_${this.period.to}`;
    },
  },

  watch: {
    "period.from": "load",
    "period.to": "load",
    restaurantId: "load",
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const [overviewRes, servicesRes] = await Promise.all([
          restaurantFinanceService.getFinancialOverview(this.restaurantId, {
            from: this.period.from,
            to: this.period.to,
          }),
          restaurantService.getRestaurantServices(this.restaurantId).catch(() => null),
        ]);
        const payload = overviewRes.data?.data || overviewRes.data || {};
        this.overview = parseOverview(payload, this.restaurantCurrency);

        this.services =
          servicesRes?.data?.services || servicesRes?.data?.data?.services || [];
        // Dinamika isplate je na nivou ugovora, pa je ista za sve aktivne usluge.
        const withContract = this.services.find(
          (s) => s.is_active && s.contract?.payout_schedule
        );
        this.contract = withContract ? withContract.contract : null;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_overview_load_error"));
      } finally {
        this.loading = false;
      }
    },

    shortDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}.`;
    },

    // Smjer iz direction (DEBIT = na teret) ako ga backend šalje, inače predznak iznosa.
    activityAmount(activity) {
      const amount = Number(activity.amount) || 0;
      return activity.direction === "DEBIT" ? -Math.abs(amount) : amount;
    },

    // Naslov je gotov s backenda; "#4365" u njemu postaje link na narudžbu. Iznos na kraju naslova
    // ("... – 21.00 KM") se skida kad se iznos već prikazuje desno.
    activityParts(activity) {
      let title = String(activity.title || "");
      if (activity.amount != null) {
        title = title.replace(/\s*[–-]\s*-?[\d.,]+\s*[^\s\d#]{1,4}\.?(?=\s|$)/, "");
      }
      const parts = [];
      let last = 0;
      title.replace(/#(\d+)/g, (match, id, index) => {
        if (index > last) parts.push({ text: title.slice(last, index) });
        parts.push({ text: match, orderId: id });
        last = index + match.length;
        return match;
      });
      if (last < title.length) parts.push({ text: title.slice(last) });
      if (!parts.some((p) => p.orderId) && activity.source_id) {
        parts.push({ text: ` #${activity.source_id}`, orderId: activity.source_id });
      }
      return parts;
    },

    openLines(row) {
      this.linesRow = row;
      this.linesOpen = true;
    },

    openOrder(id) {
      this.orderId = id;
      this.orderOpen = true;
    },

    getExportData() {
      const o = this.overview;
      const rows = [];
      if (o.opening !== null) rows.push([this.$t("finance_opening_balance"), o.opening]);
      o.rows.forEach((row) => rows.push([row.label, row.value]));
      if (o.closing !== null) rows.push([this.$t("finance_closing_balance"), o.closing]);
      return {
        headers: [
          this.$t("finance_description"),
          `${this.$t("finance_amount")} (${o.currency})`,
        ],
        rows,
      };
    },
  },
};
</script>

<style scoped>
.earn-title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
}

.earn-label {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.earn-value {
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 2px 0 6px;
}

.earn-hint {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.62);
}

.earn-link {
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 959px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.kpi-box {
  border: 1px solid #e6e8ec;
  border-radius: 8px;
  padding: 12px 14px;
  min-width: 0;
}

.kpi-box-label {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
}

.kpi-box-value {
  font-size: 1.25rem;
  font-weight: 800;
  margin: 2px 0;
  overflow-wrap: anywhere;
}

.kpi-box-note {
  font-size: 0.74rem;
  color: rgba(0, 0, 0, 0.55);
}

.earn-note {
  display: flex;
  align-items: flex-start;
  background: #f5f6f8;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.65);
}

.activity-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #eceef2;
  font-size: 0.88rem;
}

.activity-row:last-of-type {
  border-bottom: none;
}

.activity-time {
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.activity-title {
  flex: 1;
}

.activity-amount {
  font-weight: 600;
  white-space: nowrap;
}
</style>
